import { Injectable } from '@angular/core';
import { assertNonNullable } from '@examdojo/util/assert';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, withLatestFrom } from 'rxjs';
import { AssetTreeItem, AssetTreeNode, AssetTreeNodeType } from '../model/asset-tree-node.model';
import { TopicsTreeService } from '../topics-tree.service';
import {
  AssetTreeBuilderService,
  closestDirectory,
  closestParent,
  filterOutCommonNodes,
  findInTree,
  getPathRelativeToAssetsContainer,
  getRootNode,
  isVisibleNode,
  traverseTree,
} from './asset-tree-builder';

@UntilDestroy()
@Injectable()
export class AssetExplorerService {
  constructor(
    private readonly assetTreeBuilderService: AssetTreeBuilderService,
    private readonly topicsTreeService: TopicsTreeService,
  ) {}

  private readonly newDirectoryPlaceholder$$ = new BehaviorSubject<string | undefined>(undefined);
  readonly newDirectoryPlaceholder$ = this.newDirectoryPlaceholder$$.asObservable();

  private readonly readonlyMode$$ = new BehaviorSubject<boolean>(false);
  readonly readonlyMode$ = this.readonlyMode$$.asObservable();

  /**
   * The root node of the asset tree.
   * It contains both - the assets container and the favorites container.
   */
  readonly rootNode$ = this.assetTreeBuilderService.rootNode$;

  /**
   * The source directory node. All assets and temporary directories are children of this node.
   */
  readonly sourceDirectoryNode$ = this.assetTreeBuilderService.sourceDirectoryNode$;

  private readonly focusedNode$$ = new BehaviorSubject<AssetTreeNode | undefined>(undefined);
  readonly focusedNode$ = this.focusedNode$$.asObservable();

  readonly activeNode$: Observable<AssetTreeNode | undefined> = combineLatest([
    this.sourceDirectoryNode$,
    this.topicsTreeService.activeTopicPath$,
  ]).pipe(
    withLatestFrom(this.focusedNode$),
    map(([[sourceDirectoryNode, activeTopicPath], focusedNode]) => {
      if (!activeTopicPath || !sourceDirectoryNode) {
        return undefined;
      }

      if (focusedNode?.path === activeTopicPath) {
        return focusedNode;
      }

      return findInTree(sourceDirectoryNode, (node) => !!node.asset && node.path === activeTopicPath);
    }),
  );

  private readonly clipboard$$ = new BehaviorSubject<{ nodes: AssetTreeNode[] } | null>(null);
  readonly clipboard$ = this.clipboard$$.asObservable();

  readonly nodes$ = this.rootNode$.pipe(
    map((tree) => {
      const data: AssetTreeNode[] = [];

      traverseTree(tree, (node) => {
        if (isVisibleNode(node)) {
          data.push(node);
        }
      });

      return data;
    }),
  );

  setReadonlyMode(readonly: boolean) {
    this.readonlyMode$$.next(readonly);
  }

  isClipboardEmpty() {
    return this.clipboard$$.getValue() === null;
  }

  clearNewDirectoryPlaceholder() {
    this.newDirectoryPlaceholder$$.next(undefined);
  }

  setFocusedNode(node?: AssetTreeNode) {
    this.focusedNode$$.next(node);
  }

  getFocusedNode() {
    return this.focusedNode$$.getValue();
  }

  async deleteNodes(parentNodes: AssetTreeNode[]) {
    const tempDirs = new Set(this.assetTreeBuilderService.getTemporaryDirectories());
    const assetsToDelete: AssetTreeItem[] = [];

    for (const parentNode of parentNodes) {
      traverseTree(parentNode, (node) => {
        if (node.nodeType === AssetTreeNodeType.TemporaryDirectory) {
          tempDirs.delete(node.path);
          return;
        }

        if (!node.asset) {
          return;
        }

        assetsToDelete.push(node.asset);
      });
    }

    const uniqueAssetIds = Array.from(new Set(assetsToDelete.map((asset) => asset.id)));
    this.assetTreeBuilderService.setTemporaryDirectories(Array.from(tempDirs));
    await this.topicsTreeService.delete(uniqueAssetIds); // TODO
  }

  getNodesAllowedToBeMoved(nodesToPaste: AssetTreeNode[], targetNode: AssetTreeNode) {
    // If the target node is a child of the dragged nodes, do nothing.
    if (
      nodesToPaste.some((node) => {
        return node && targetNode.path.includes(node.path) && node.path !== targetNode.path;
      })
    ) {
      return [];
    }

    const nodes = filterOutCommonNodes(nodesToPaste);

    return (
      nodes
        // Filter out children assets.
        .filter((n) => !n.parent?.asset)
        // Filter out incorrect node types.
        .filter(
          (n) => n.nodeType === AssetTreeNodeType.TopicLevel2 || n.nodeType === AssetTreeNodeType.TemporaryDirectory,
        )
    );
  }

  /*cutToClipboard(nodes: AssetTreeNode[]) {
    // TODO: Ensure that the object can be cut.
    // TODO: Mark node as cut.
    this.clipboard$$.next({ nodes });
  }

  clearClipboard() {
    this.clipboard$$.next(null);
  }

  async pasteFromClipboard(pastePlace: AssetTreeNode) {
    const clipboard = this.clipboard$$.getValue();

    if (!clipboard) {
      return;
    }

    const pastePlaceDir = closestDirectory(pastePlace);

    if (!pastePlaceDir) {
      this.toastService.error({
        title: 'Cannot move assets to given place.',
      });

      return;
    }

    const nodes = this.getNodesAllowedToBeMoved(clipboard.nodes, pastePlaceDir);
    const disallowedByDbtNodes = this.getDisallowedNodeAssetsToMoveByDbtConfig(nodes, pastePlaceDir);

    if (disallowedByDbtNodes.length) {
      const disallowedByDbtNodesNames = disallowedByDbtNodes.map((node) => highlightValue(node.modelName)).join(', ');

      this.toastService.error({
        title: 'Cannot move assets.',
        description: `The following assets cannot be moved to the selected place because they are not allowed by DBT project configuration:`,
        markdown: disallowedByDbtNodesNames,
      });

      return;
    }

    await this.move(nodes, pastePlaceDir.path);
    // Clear the clipboard after the successful move.
    // We don't want to allow pasting later as "move" and "copy" operations are semantically very different in our app.
    this.clipboard$$.next(null);
  }*/

  /**
   * Moves the asset tree nodes to the new place. `to` and `from` should be relative paths.
   * If `from` is skipped, the current path of the asset is used.
   */
  async move(movedNodes: AssetTreeNode[], to: string, from?: string) {
    const tempDirs = new Set(this.assetTreeBuilderService.getTemporaryDirectories());
    const toMove: Array<{ id: AssetTreeItem['id']; to: string }> = [];

    for (const movedNode of movedNodes) {
      const rootNode = getRootNode(movedNode);
      const fromNode = from ? findInTree(rootNode, (n) => n.path === from) : movedNode.parent;
      assertNonNullable(fromNode, 'fromNode');

      // Prevent moving to the same place, to the child node, or to a direct parent.
      if (to.startsWith(`${movedNode.path}/`) || to === fromNode.path || movedNode.path === to) {
        continue;
      }

      // Traverse the tree to find the assets and temporary directories that need to be moved.
      traverseTree(movedNode, (node) => {
        assertNonNullable(fromNode, 'fromNode');
        // update temporary directories that were present in the moved subtree.
        if (node.nodeType === AssetTreeNodeType.TemporaryDirectory) {
          const newPath = node.path.replace(fromNode.path, to);
          tempDirs.delete(node.path);
          tempDirs.add(newPath);
          return;
        }

        // Skip the asset if it's a child of another asset.
        if (!node.asset || node.parent?.asset) {
          return;
        }

        let nodeDirectory = node;
        while (nodeDirectory.asset && nodeDirectory.parent) {
          nodeDirectory = nodeDirectory.parent;
        }

        toMove.push({
          id: node.asset.id,

          // move the asset to the new place, while trying to preserve the directory structure (relative to the moved node).
          to: nodeDirectory.path
            .replace(fromNode.path, to)
            // TODO - work on the nodes instead of a path.
            .replace(/^ROOT\/assets\//, ''),
        });
      });

      const closestVisibleParentDirectory =
        movedNode.parent && closestParent(movedNode.parent, (item) => !item.asset && isVisibleNode(item));

      if (closestVisibleParentDirectory) {
        // Add a temporary directory to allow users quickly revert the move operation.
        // This only applies to the visible parent directory of the moved node that may be gone after moving
        // if all the assets inside it were moved.
        tempDirs.add(closestVisibleParentDirectory.path);
      }
    }

    await this.topicsTreeService.move(toMove);

    this.assetTreeBuilderService.setTemporaryDirectories(Array.from(tempDirs));
  }

  renameNode(node: AssetTreeNode) {
    const id = node.asset?.id;
    assertNonNullable(id, 'asset id');
    this.topicsTreeService.rename(id);
  }

  createAssetNode(node = this.getFocusedNode()) {
    const directoryNode = closestDirectory(node);
    const path = directoryNode && getPathRelativeToAssetsContainer(directoryNode);

    this.topicsTreeService.create(path);
  }

  async createRootDirectory() {
    const sourceDirectoryNode = await firstValueFrom(this.assetTreeBuilderService.sourceDirectoryNode$);
    assertNonNullable(sourceDirectoryNode, 'sourceDirectoryNode');

    this.newDirectoryPlaceholder$$.next(sourceDirectoryNode.path);
  }

  async createRootAsset() {
    throw new Error('Not implemented');
  }

  createNewDirectory(node: AssetTreeNode) {
    const dirNode = closestDirectory(node);

    if (!dirNode) {
      return;
    }

    this.newDirectoryPlaceholder$$.next(dirNode.path);
  }

  addTemporaryDirectory(path: string) {
    const tempDirs = new Set(this.assetTreeBuilderService.getTemporaryDirectories());
    tempDirs.add(path);
    this.assetTreeBuilderService.setTemporaryDirectories(Array.from(tempDirs));
  }
}
